import {
  fakeOptimizeGoal,
  OptimizeGoal,
  OptimizationGoalItem,
  AdgroupFormData,
  CampaignFormData,
  CampaignObjectiveType as Objective,
  VOStatus,
  AppProfilePageType,
  VOStatusConfig,
  objectiveOptimizationGoal,
  ExternalAction,
  CreateMode,
} from '@tt4b/ttam-enums';
import {
  useAdgroupIos14,
  useAllowlist,
  useStdAdgroupAmazon,
  useStdAdgroupIs,
  useStdObjectiveType,
  useStdAdgroupCbo,
  useSplitTestEnabled,
} from '@tt4b/ttam-hooks';
import { Ref, computed, ref } from '@vue/composition-api';
import {
  optimizeGoalOptions,
  voStatusAppOptions,
  voStatusWebsiteOptions,
  shopLiveObjectiveOptimizationGoalOptions,
  shopVideoObjectiveOptimizationGoalOptions,
  showcaseOptimizationGoalOptions,
  psaOptimizationGoalOptions,
} from '../consts';
import { OptimizationGoalOptionItem } from '../types';
import { i18n } from '@tt4b/ttam-i18n';
import VueI18n from 'vue-i18n';

// eslint-disable-next-line max-lines-per-function

/**
 * 根据 Optimization Goal 的 key 或者相应的 list
 */
export const getTargetOptGoalOptions = (keys: OptimizeGoal[]): OptimizationGoalItem[] =>
  optimizeGoalOptions().filter(v => keys.includes(v.value));

export const useAppInstallOptGoalOptionsGet = (props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  voStatus?: Ref<VOStatusConfig>;
  originList: number[];
}) => {
  const { campaignData, adgroupData, originList, voStatus } = props;
  let list = originList;
  const { isVoPangleInstallRoasWhite, isVoPangleInstallStandardWhite, isAeoCboWhite, isAppProfileAutoSelectedWhite } =
    useAllowlist();
  const { isAmazonAppinstall } = useStdAdgroupAmazon({
    campaignFormData: campaignData,
  });
  const isAppProfilePageOpen = adgroupData.value.adAppProfilePageType === AppProfilePageType.on;
  const { isOnlyPangle } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isCBOVO, isCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  if (isCBO.value && !isCBOVO.value) {
    list = [OptimizeGoal.INSTALL].concat(
      isAeoCboWhite.value && !isAppProfilePageOpen ? [OptimizeGoal.IN_APP_EVENT] : [],
    );
    return list;
  }

  if (isAppProfilePageOpen && !isAppProfileAutoSelectedWhite.value) {
    list = [OptimizeGoal.INSTALL].concat([OptimizeGoal.CLICK, OptimizeGoal.IN_APP_EVENT]);
    return list;
  }
  // 检查VO是否开白
  const isInstallNotSupportPangle =
    (!isOnlyPangle.value && voStatus.value.valueOptimizerStatus === VOStatus.NOTSUPPORT) ||
    (isOnlyPangle.value && !isVoPangleInstallRoasWhite.value && !isVoPangleInstallStandardWhite.value);
  if (
    (isInstallNotSupportPangle && voStatus.value.valueOptimizerStatus === VOStatus.NOTSUPPORT) ||
    isAmazonAppinstall.value
  ) {
    list = list.filter(item => item !== OptimizeGoal.VALUE);
  }

  return list;
};

export const useConversionOptGoalOptionsGet = (props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  voStatus: Ref<VOStatusConfig>;
  originList?: OptimizeGoal[];
}) => {
  const { campaignData, adgroupData, originList, voStatus } = props;
  let list = (originList || objectiveOptimizationGoal?.[Objective.CONVERSION]) ?? [];
  const { isAeoCboWhite } = useAllowlist();
  const { isAmazonAppinstall } = useStdAdgroupAmazon({
    campaignFormData: campaignData,
  });
  const { isConversionApp, isWebsite } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isCBOVO, isCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  // CBO 仅支持 CONVERT
  if (isCBO.value && !isCBOVO.value) {
    if (isConversionApp.value) {
      list = isAeoCboWhite.value ? [OptimizeGoal.IN_APP_EVENT] : [];
    } else {
      list = [OptimizeGoal.CONVERT];
    }
    return list;
  }

  // 检查Website VO是否开白
  if (isWebsite.value && (voStatus.value.valueOptimizerStatus === VOStatus.NOTSUPPORT || isAmazonAppinstall.value)) {
    list = list.filter(item => item !== OptimizeGoal.VALUE);
    return list;
  }

  // Conversion APP 支持IN_APP_EVENT、开白VALUE
  if (isConversionApp.value) {
    const isConversionAppSupoortVO =
      voStatus.value.valueOptimizerStatus !== VOStatus.NOTSUPPORT && !isAmazonAppinstall.value;
    list = [OptimizeGoal.IN_APP_EVENT, ...(isConversionAppSupoortVO ? [OptimizeGoal.VALUE] : [])];
    return list;
  }

  return list;
};

export const useTrafficOptGoalOptionsGet = (props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  originList?: OptimizeGoal[];
}) => {
  const { campaignData, adgroupData, originList } = props;
  let list = (originList || objectiveOptimizationGoal?.[Objective.TRAFFIC]) ?? [];
  const { isHasLPVForPangleWhite, isHasLpvWhite } = useAllowlist();

  const { isOnlyPangle, isWebsite } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  // TODO 待 splitest支持
  const isSplitTest = ref(false);
  const isLPVAvailable =
    isWebsite.value &&
    !campaignData.value.abTest &&
    !isSplitTest.value &&
    (isOnlyPangle.value ? isHasLPVForPangleWhite.value : isHasLpvWhite.value);
  if (!isLPVAvailable) {
    list = list.filter(item => item !== OptimizeGoal.LANDING_PAGE_VIEW);
  }

  return list;
};

const toItem = (o: OptimizationGoalItem | OptimizationGoalOptionItem) =>
  ({
    ...o,
    labelInfoVisible: Boolean(o.labelInfo),
  } as OptimizationGoalOptionItem);

const filterValueItem = (o: OptimizationGoalItem | OptimizationGoalOptionItem, voStatus: VOStatusConfig) => {
  if (o.value === OptimizeGoal.VALUE) {
    return voStatus?.valueOptimizerStatus === VOStatus.QUALIFIED;
  }
  return true;
};

// live & shop & showcase => LSS
export const useProductSalesLSSOptGoalOptionsGet = (props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  voStatus: Ref<VOStatusConfig>;
}) => {
  const { campaignData, adgroupData, voStatus } = props;

  const { isLiveAds, isStandardAdsWithShop, isStandardAdsWithShowcase, isPSA } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  if (isLiveAds.value) {
    return shopLiveObjectiveOptimizationGoalOptions()
      .filter(o => {
        if (o.value === fakeOptimizeGoal.INITATE_CHECKOUT) {
          return true;
        } else if (o.value === OptimizeGoal.VALUE) {
          return voStatus?.value?.valueOptimizerStatus === VOStatus.QUALIFIED;
        }
        // 判断shoptype，如果是混场就不判断了
        if (o.enableShopTypes && adgroupData.value.shopType) {
          return o.enableShopTypes.includes(adgroupData.value.shopType);
        }

        return true;
      })
      .map(toItem);
  }

  if (isStandardAdsWithShop.value) {
    return shopVideoObjectiveOptimizationGoalOptions()
      .filter(o => {
        if (o.value === OptimizeGoal.VALUE) {
          return voStatus?.value?.valueOptimizerStatus === VOStatus.QUALIFIED;
        } else {
          return o.enableShopTypes.includes(adgroupData.value.shopType);
        }
      })
      .map(toItem);
  }

  if (isStandardAdsWithShowcase.value) {
    return showcaseOptimizationGoalOptions()
      .filter(o => filterValueItem(o, voStatus.value))
      .map(toItem);
  }

  if (isPSA.value) {
    return psaOptimizationGoalOptions()
      .filter(o => filterValueItem(o, voStatus.value))
      .map(toItem);
  }

  return [];
};

export const handleCatalogWebsiteOptGoalOptions = (props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  originList?: OptimizeGoal[];
}) => {
  const { campaignData, adgroupData, originList } = props;

  const { isOnlyPangle } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { isSplitTestLevelEnabled, isCampaignSplitTestEnabled } = useSplitTestEnabled({ adgroupData, campaignData });

  const { isProductSalesOpenLoopLPVWhite } = useAllowlist();

  const hasBindPixel = adgroupData.value.adRefPixelId && adgroupData.value.externalAction;

  let list = originList || [];

  // website支持CLICK,CONVERT,VALUE,LANDING_PAGE_VIEW
  const supportList = [OptimizeGoal.CLICK, OptimizeGoal.CONVERT, OptimizeGoal.VALUE];

  /** LPV通过白名单控制 */
  if (!isCampaignSplitTestEnabled.value && isProductSalesOpenLoopLPVWhite.value) {
    supportList.push(OptimizeGoal.LANDING_PAGE_VIEW);
  }

  list = list.filter((o: OptimizeGoal) => supportList.includes(o));

  // 在 split test 层级
  if (isSplitTestLevelEnabled.value) {
    // 因为有pixel不能选 click，没pixel不能选convert
    list = list.filter(o => ![hasBindPixel ? OptimizeGoal.CLICK : OptimizeGoal.CONVERT].includes(o));
  }

  /**
   *  pangle支持web vo自动解锁，未选中pixel和complete payment的时候展示一个禁用的value
   *  禁用的逻辑是后端返回200，在packages/libs/ttam-ui/src/components/adgroup/optimization-goal/consts.ts:276中把
   *  200的状态改为disable
   */
  if (!isOnlyPangle.value && (!hasBindPixel || adgroupData.value.externalAction !== ExternalAction.shopping)) {
    // 没有勾选pixel，或者选择的浅层事件不是complete payment，就没有Value
    list = list.filter(o => ![OptimizeGoal.VALUE].includes(o));
  }
  return list;
};

export const useProductSalesCatalogOptGoalOptionsGet = (props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  voStatus: Ref<VOStatusConfig>;
  originList?: OptimizeGoal[];
  createMode?: Ref<CreateMode>;
}) => {
  const { campaignData, adgroupData, originList, voStatus, createMode } = props;
  const { isSupportDestinationVisitWhite } = useAllowlist();
  let list = (originList || objectiveOptimizationGoal?.[Objective.DPA]) ?? [];

  const { isWebsite, isAppDownload, isProductRetargeting, isOnlyTiktok } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { isDedicateCampaign } = useAdgroupIos14({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  if (isWebsite.value) {
    list = handleCatalogWebsiteOptGoalOptions({
      campaignData,
      adgroupData,
      originList: list,
    });
  }

  if (isAppDownload.value) {
    const { isSplitTestEnabled } = useSplitTestEnabled({ adgroupData, campaignData });

    let appSupportList: OptimizeGoal[] = [];
    if (isProductRetargeting.value) {
      // app重定向非ios14支持 IN_APP_EVENT，CLICK， VALUE
      appSupportList = [OptimizeGoal.IN_APP_EVENT, OptimizeGoal.CLICK, OptimizeGoal.VALUE];
    } else {
      // app非重支持 IN_APP_EVENT，CLICK，VALUE，INSTALL
      appSupportList = [OptimizeGoal.INSTALL, OptimizeGoal.IN_APP_EVENT, OptimizeGoal.CLICK, OptimizeGoal.VALUE];
      // 复制时如果禁用就不展示，因为 apply destination visit 会设置版位，而复制不允许修改版位
      const isDestinationVisitSelectable = isOnlyTiktok.value;
      const isCurrentModeSupport = createMode?.value === CreateMode.COPY ? isDestinationVisitSelectable : true;
      if (
        isSupportDestinationVisitWhite.value &&
        !isSplitTestEnabled.value &&
        !isDedicateCampaign.value &&
        isCurrentModeSupport
      ) {
        appSupportList.push(OptimizeGoal.DESTINATION_VISIT as OptimizeGoal);
      }
    }
    list = list.filter(o => appSupportList.includes(o));

    if (voStatus?.value?.valueOptimizerStatus === VOStatus.NOTSUPPORT) {
      list = list.filter(o => o !== OptimizeGoal.VALUE);
    }
  }

  return list;
};

export const useProductSalesClaOptionsGet = (props: { adgroupData: Ref<AdgroupFormData> }) => {
  const { adgroupData } = props;

  const { isCLAPixelOptionalWhite } = useAllowlist();
  const hasBindPixel = computed(() => adgroupData.value.adRefPixelId && adgroupData.value.externalAction);
  if (isCLAPixelOptionalWhite.value && !hasBindPixel.value) {
    return [OptimizeGoal.CLICK];
  }
  return [OptimizeGoal.CONVERT];
};

// eslint-disable-next-line no-unused-vars
type HandleOptGoalFn = (props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  voStatus: Ref<VOStatusConfig>;
  originList: OptimizeGoal[];
  createMode?: Ref<CreateMode>;
}) => OptimizeGoal[];

export const GetObjectiveHandleOptGoalMap: {
  [key: string]: HandleOptGoalFn;
} = {
  [Objective.APPINSTALL]: useAppInstallOptGoalOptionsGet,
  [Objective.CONVERSION]: useConversionOptGoalOptionsGet,
  [Objective.TRAFFIC]: useTrafficOptGoalOptionsGet,
  [Objective.PRODUCT_SALES]: props => {
    const { adgroupData, campaignData, voStatus, originList, createMode } = props;

    const { isStandardAdsWithCatalog, isCLA } = useStdAdgroupIs({
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
    });

    /**
     * @link https://bytedance.feishu.cn/sheets/shtcnU55FsLlqiRt9Jix1OkSmQc 最原始文档
     */
    if (isStandardAdsWithCatalog.value) {
      return useProductSalesCatalogOptGoalOptionsGet({
        adgroupData,
        campaignData,
        originList,
        voStatus,
        createMode,
      });
    } else if (isCLA.value) {
      return useProductSalesClaOptionsGet({
        adgroupData,
      });
    } else {
      return [];
    }
  },
};

export const handleOptGoalItemExtraOptions = (props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  voStatus: Ref<VOStatusConfig>;
  optGoalOpts: OptimizationGoalItem[];
}) => {
  const { adgroupData, campaignData, voStatus, optGoalOpts } = props;
  const { isAppInstall, isConversion, isTraffic, isProductSales } = useStdObjectiveType({
    campaignFormData: campaignData,
  });
  const { isWebsite, isOnlyTiktok } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const voStatusConfig = isWebsite.value
    ? voStatusWebsiteOptions()[voStatus.value?.valueOptimizerStatus]
    : voStatusAppOptions()[voStatus.value?.valueOptimizerStatus];
  // 是否选择 pixel
  const isUsePixelChecked = Boolean(adgroupData.value.adRefPixelId);

  const isLpvDisabled =
    isUsePixelChecked ||
    Boolean(adgroupData.value?.retargetingTags?.length || adgroupData.value?.retargetingTagsExclude?.length);
  const isClickDisabled = isUsePixelChecked;
  const destinationVisitDisabled = !isOnlyTiktok.value;

  const getOptimizeGoalItemActionStatus = (
    item: OptimizationGoalItem,
  ): {
    disabled: boolean;
    showPopover: boolean;
    popoverText?: VueI18n.TranslateResult;
    popoverSubText?: VueI18n.TranslateResult;
  } => {
    switch (item.value) {
      case OptimizeGoal.VALUE: {
        return {
          disabled: voStatusConfig?.disabled,
          showPopover: voStatusConfig?.showPopover,
        };
      }
      case OptimizeGoal.LANDING_PAGE_VIEW: {
        return {
          disabled: isLpvDisabled,
          showPopover: isLpvDisabled,
          popoverText: i18n.t('adgroup_optimize_click_disable_tips'),
        };
      }
      case OptimizeGoal.CLICK: {
        return {
          disabled: isClickDisabled,
          showPopover: isClickDisabled,
          popoverText: i18n.t('adgroup_optimize_click_disable_tips'),
        };
      }
      case OptimizeGoal.DESTINATION_VISIT: {
        return {
          disabled: destinationVisitDisabled,
          showPopover: destinationVisitDisabled,
          popoverText: i18n.t('adgroup_optimize_destination_visit_disable_title'),
          popoverSubText: i18n.t('adgroup_optimize_destination_visit_disable_tips'),
        };
      }
      default:
        return {
          disabled: false,
          showPopover: false,
        };
    }
  };

  const getLabelInfoVisible = (item: OptimizationGoalItem) =>
    (isAppInstall.value || isConversion.value || isTraffic.value || isProductSales.value) && Boolean(item.labelInfo);

  return optGoalOpts?.map?.(o => {
    const { disabled, showPopover, popoverText = '', popoverSubText = '' } = getOptimizeGoalItemActionStatus(o);

    return {
      ...o,
      disabled,
      showPopover,
      popoverText,
      popoverSubText,
      labelInfoVisible: getLabelInfoVisible(o),
    };
  });
};
